/**
 * Здоровье клиента (TRK-5): js_error и api_error с капом 5 на вкладку,
 * Core Web Vitals через fetch keepalive с Bearer.
 */
import { onCLS, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { session } from '@/lib/storage';
import { track, trackKeepalive } from './tracker';

const JS_ERROR_COUNT_KEY = 'shotbook_js_error_count';
const API_ERROR_COUNT_KEY = 'shotbook_api_error_count';
const MAX_PER_SESSION = 5;

let errorTrackingInstalled = false;
let vitalsInstalled = false;
let reportingJsError = false;

function clip(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  return value.slice(0, max);
}

/** Кап репортов на вкладку; false — лимит выбран или хранилище недоступно. */
function takeReportSlot(key: string): boolean {
  const raw = session.get(key);
  const count = raw ? parseInt(raw, 10) || 0 : 0;
  if (count >= MAX_PER_SESSION) return false;
  session.set(key, String(count + 1));
  return true;
}

export function reportApiError(metadata: {
  endpoint_pattern: string;
  method: string;
  status: number;
  duration_ms: number;
}): void {
  if (typeof window === 'undefined') return;
  if (!takeReportSlot(API_ERROR_COUNT_KEY)) return;
  track('api_error', { metadata });
}

function reportJsError(metadata: Record<string, unknown>): void {
  if (reportingJsError) return;
  if (!takeReportSlot(JS_ERROR_COUNT_KEY)) return;
  reportingJsError = true;
  try {
    track('js_error', { metadata });
  } finally {
    reportingJsError = false;
  }
}

export function installGlobalErrorTracking(): void {
  if (typeof window === 'undefined' || errorTrackingInstalled) return;
  errorTrackingInstalled = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    reportJsError({
      message: clip(event.message, 300),
      source: clip(event.filename, 300),
      line: typeof event.lineno === 'number' ? event.lineno : undefined,
      col: typeof event.colno === 'number' ? event.colno : undefined,
      stack: clip(event.error instanceof Error ? event.error.stack : undefined, 1500),
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason;
    let message: string;
    try {
      message = reason instanceof Error ? reason.message : String(reason);
    } catch {
      message = Object.prototype.toString.call(reason);
    }
    reportJsError({
      message: clip(message, 300),
      stack: clip(reason instanceof Error ? reason.stack : undefined, 1500),
      kind: 'unhandledrejection',
    });
  });
}

export function initWebVitals(): void {
  if (vitalsInstalled || typeof window === 'undefined') return;
  vitalsInstalled = true;

  const report = (metric: Metric) => {
    trackKeepalive('web_vital', {
      metadata: {
        metric: metric.name,
        value:
          metric.name === 'CLS' ? Math.round(metric.value * 1000) / 1000 : Math.round(metric.value),
        rating: metric.rating,
        page: window.location.pathname,
      },
    });
  };

  onLCP(report);
  onCLS(report);
  onINP(report);
  onTTFB(report);
}
